import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithRedirect,
  getRedirectResult,
  signInWithCustomToken,
  GoogleAuthProvider,
  signOut,
  onAuthStateChanged,
  User as FirebaseUser,
} from 'firebase/auth';
import { auth } from '../config/firebase';
import apiService from '../services/api';
import { setSharedAuthToken, getSharedAuthToken, clearSharedAuthToken } from '../utils/sharedAuth';
import { analytics } from '../utils/analytics';

interface AuthUser {
  // Backend user UUID — stable across token refreshes + works for
  // Mosan auto-login users who may have synthetic / empty email.
  // Use this (not email) as the swap signal when a screen needs to
  // refetch on account change.
  id?: string;
  name: string;
  email: string;
  photoURL?: string;
  // 'firebase' for normal email/Google sign-ins, 'mosan' for users created
  // via the partner WebView auto-login. Used to suppress the in-app logout
  // button — Mosan users have no way to sign back in inside the WebView.
  authSource?: string;
}

interface AuthContextType {
  user: AuthUser | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string) => Promise<void>;
  loginWithGoogle: () => Promise<void>;
  loginWithCustomToken: (token: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const redirectChecked = useRef(false);

  // Sync user with backend and store token for API calls
  const syncWithBackend = useCallback(async (firebaseUser: FirebaseUser) => {
    // Force token refresh to check if refresh token is still valid
    // (will fail if tokens were revoked via backend logout)
    let idToken: string;
    try {
      idToken = await firebaseUser.getIdToken(true);
    } catch {
      // Refresh token revoked — force logout
      clearSharedAuthToken();
      await signOut(auth);
      localStorage.removeItem('auth_token');
      localStorage.removeItem('auth_user');
      setUser(null);
      return;
    }
    localStorage.setItem('auth_token', idToken);

    // Call backend login endpoint (auto-registers if needed)
    try {
      const response = await apiService.firebaseLogin();
      const backendUser = response.data?.user;
      const userData: AuthUser = {
        id: backendUser?.id || firebaseUser.uid,
        name: backendUser?.name || firebaseUser.displayName || firebaseUser.email?.split('@')[0] || 'User',
        email: backendUser?.email || firebaseUser.email || '',
        photoURL: backendUser?.avatar_url || firebaseUser.photoURL || undefined,
        authSource: backendUser?.auth_source || undefined,
      };
      localStorage.setItem('auth_user', JSON.stringify(userData));
      setUser(userData);

      // Set shared auth cookie for cross-app login
      apiService.getCustomToken().then((token) => {
        setSharedAuthToken(token);
        localStorage.setItem('shared_auth_active', '1');
      }).catch(() => {});
    } catch {
      // Backend sync failed, still show user from Firebase data
      const userData: AuthUser = {
        id: firebaseUser.uid,
        name: firebaseUser.displayName || firebaseUser.email?.split('@')[0] || 'User',
        email: firebaseUser.email || '',
        photoURL: firebaseUser.photoURL || undefined,
      };
      localStorage.setItem('auth_user', JSON.stringify(userData));
      setUser(userData);
    }
  }, []);

  // Listen to Firebase auth state changes
  useEffect(() => {
    // Handle pending redirect result (from signInWithRedirect)
    getRedirectResult(auth)
      .then((result) => {
        if (result?.user) {
          syncWithBackend(result.user);
          redirectChecked.current = true;
        }
      })
      .catch((error) => {
        console.error('Redirect sign-in error:', error);
      })
      .finally(() => {
        redirectChecked.current = true;
        // If no Firebase user after redirect check, try cross-app handoff
        // before falling through to logged-out state. Order:
        //   1. ?auth=<customToken> query param (explicit deep-link from ecommerce)
        //   2. shared auth cookie (passive cross-app login)
        // Both paths keep loading=true until signInWithCustomToken resolves so
        // the navbar skeleton stays up — the Login button never flashes.
        if (!auth.currentUser) {
          const authParam = new URLSearchParams(window.location.search).get('auth');
          if (authParam) {
            signInWithCustomToken(auth, authParam)
              .catch(() => {
                clearSharedAuthToken();
                setUser(null);
                setLoading(false);
              })
              .finally(() => {
                // Strip the token from the URL so a back-nav can't replay it.
                const url = new URL(window.location.href);
                url.searchParams.delete('auth');
                window.history.replaceState({}, '', url.toString());
              });
            return;
          }
          const sharedToken = getSharedAuthToken();
          if (sharedToken) {
            signInWithCustomToken(auth, sharedToken).catch(() => {
              clearSharedAuthToken();
              setUser(null);
              setLoading(false);
            });
          } else {
            setUser(null);
            setLoading(false);
          }
        }
      });

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      // Don't process null until redirect check is done
      if (!redirectChecked.current && !firebaseUser) {
        return;
      }
      // If user triggered logout, don't auto-login even if Firebase state lingers
      if (firebaseUser && localStorage.getItem('logout_pending')) {
        localStorage.removeItem('logout_pending');
        signOut(auth).catch(() => {});
        return;
      }
      // If logged in but shared cookie was cleared (other app logged out), sign out
      if (firebaseUser && !getSharedAuthToken() && localStorage.getItem('shared_auth_active')) {
        localStorage.removeItem('shared_auth_active');
        signOut(auth).catch(() => {});
        localStorage.removeItem('auth_token');
        localStorage.removeItem('auth_user');
        setUser(null);
        setLoading(false);
        return;
      }
      if (firebaseUser) {
        await syncWithBackend(firebaseUser);
      } else {
        localStorage.removeItem('auth_token');
        localStorage.removeItem('auth_user');
        setUser(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, [syncWithBackend]);

  // Refresh token periodically (every 50 minutes, Firebase tokens expire in 1 hour)
  useEffect(() => {
    const interval = setInterval(async () => {
      const currentUser = auth.currentUser;
      if (currentUser) {
        try {
          const token = await currentUser.getIdToken(true);
          localStorage.setItem('auth_token', token);
        } catch {
          // Token revoked or refresh failed — force logout
          await signOut(auth);
          localStorage.removeItem('auth_token');
          localStorage.removeItem('auth_user');
          setUser(null);
        }
      }
    }, 50 * 60 * 1000);

    return () => clearInterval(interval);
  }, []);

  const login = async (email: string, password: string) => {
    try {
      await signInWithEmailAndPassword(auth, email, password);
      analytics.login();
      // onAuthStateChanged will handle the rest
    } catch (error: any) {
      throw new Error(error.message || 'Login failed');
    }
  };

  const register = async (email: string, password: string) => {
    try {
      await createUserWithEmailAndPassword(auth, email, password);
      analytics.signUp();
      // onAuthStateChanged will handle the rest
    } catch (error: any) {
      throw new Error(error.message || 'Registration failed');
    }
  };

  const loginWithGoogle = async () => {
    analytics.loginWithGoogle();
    const provider = new GoogleAuthProvider();
    await signInWithRedirect(auth, provider);
  };

  // For seamless login from ecommerce via custom token
  const loginWithCustomToken = async (token: string) => {
    if (user) return; // Already logged in
    try {
      await signInWithCustomToken(auth, token);
      // onAuthStateChanged will handle the rest
    } catch (error: any) {
      console.error('Custom token login failed:', error);
    }
  };

  const logout = async () => {
    clearSharedAuthToken();
    localStorage.removeItem('shared_auth_active');
    localStorage.setItem('logout_pending', '1');
    // Revoke Firebase refresh tokens server-side BEFORE clearing the local
    // token, so the request still authenticates. This is what makes a logout
    // here propagate to the ecommerce tab — its 30s poll forces a getIdToken
    // refresh which then fails because the refresh token is revoked.
    await apiService.firebaseLogout().catch(() => {});
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_user');
    setUser(null);
    try {
      await Promise.race([
        signOut(auth),
        new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), 5000)),
      ]);
      localStorage.removeItem('logout_pending');
    } catch {
      // signOut hung or failed — reload; onAuthStateChanged will see logout_pending flag
      window.location.reload();
    }
  };

  const value = {
    user,
    loading,
    login,
    register,
    loginWithGoogle,
    loginWithCustomToken,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
